import React from 'react';
import {
    Box, CardContent,
    CardHeader,
    Divider, List, ListItemText, Tooltip,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import PaymentIcon from '@material-ui/icons/Payment';
import DateRangeIcon from '@material-ui/icons/DateRange';
import WatchLaterIcon from '@material-ui/icons/WatchLater';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardHeaderTitle, ListItemDense, ListItemIconDense, SmallAvatar, TableIcon } from '../../../../ui-kit';
import { getFullName, getInitials, localizeEnum } from '../../../../utils';
import { routePaths } from '../../../../routePaths';
import { useUser } from '../../../../utils/hooks/auth';
import { useDateTimeService } from '../../../../utils/hooks/dateTimeService';
import { BillState, PaymentMethod } from '../../../../generated/graphql';
import { BillItemProps } from './types';
import { BillAmount } from './BillAmount';

const Bill = ({ bill }: BillItemProps) => {
    const { user } = useUser();
    const { dateTimeService } = useDateTimeService();
    const { t } = useTranslation();
    const history = useHistory();

    const date = dateTimeService.localizeDate(bill.issuedUtc, user.cultureCode);
    const time = dateTimeService.localizeTime(bill.issuedUtc, user.cultureCode);

    const {
        issuedBy: { id, account: { firstName, lastName, imageUrl } },
        total,
        billNumber,
        paymentMethod,
        fiscalRecord,
        billState,
        dicountedTotalValue,
        roundedPrice,
        place,
        customPaymentMethod,
    } = bill;

    const isVoided = billState === BillState.Voided;
    const voidedBillText = t('The bill was voided');
    const placeName = place ? place.name: t('Take away');


    const fullName = getFullName({ firstName, lastName });
    const initials = getInitials({ firstName, lastName });

    const paymentMethodName = customPaymentMethod ? customPaymentMethod.name : localizeEnum(t)(paymentMethod, { PaymentMethod });
    const paidBy = t('Paid by {{paymentMethod}}', { paymentMethod: paymentMethodName });
    const navigateToProfile = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        history.push(`${routePaths.employee}/${id}`);
    };

    return (
        <Card>
            <CardHeader
                disableTypography
                title={<CardHeaderTitle>{billNumber}</CardHeaderTitle>}
            />
            <Divider variant='middle' />
            <CardContent>
                <Box position='relative'>
                    <List dense>
                        <ListItemDense>
                            <ListItemIconDense>
                                <SmallAvatar src={imageUrl || undefined}>
                                    {initials}
                                </SmallAvatar>
                            </ListItemIconDense>
                            <ListItemText primaryTypographyProps={{ color: 'primary' }} onClick={navigateToProfile} primary={fullName} />
                        </ListItemDense>
                        <ListItemDense>
                            <ListItemIconDense>
                                <WatchLaterIcon />
                            </ListItemIconDense>
                            <ListItemText primary={time} />
                            <ListItemIconDense>
                                <DateRangeIcon />
                            </ListItemIconDense>
                            <ListItemText primary={date} />
                        </ListItemDense>
                        <ListItemDense>
                            <ListItemIconDense>
                                <MonetizationOnIcon />
                            </ListItemIconDense>
                            <ListItemText
                                primary={(
                                    <BillAmount
                                        total={total}
                                        dicountedTotalValue={dicountedTotalValue}
                                        roundedPrice={roundedPrice}
                                    />
                                )}
                            />
                        </ListItemDense>
                        <ListItemDense>
                            <ListItemIconDense>
                                <PaymentIcon />
                            </ListItemIconDense>
                            <ListItemText primary={paidBy} />
                        </ListItemDense>
                        <ListItemDense>
                            <ListItemIconDense>
                                <AccountBalanceIcon />
                            </ListItemIconDense>
                            <ListItemText primary={fiscalRecord ? fiscalRecord.state : t('Not Reported')} />
                        </ListItemDense>
                        <ListItemDense>
                            <ListItemIconDense>
                                <TableIcon />
                            </ListItemIconDense>
                            <ListItemText primary={placeName} />
                        </ListItemDense>
                    </List>
                    {isVoided
                    ? (
                        <Box position='absolute' bottom={0} right={0}>
                            <Tooltip title={voidedBillText} aria-label={voidedBillText} placement='top'>
                                <WarningIcon color='error' />
                            </Tooltip>
                        </Box>
                    ) : null}
                </Box>
            </CardContent>
        </Card>
    );
};

export default Bill;
